import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { errorMessage, errorDetail, getToken } from "@/lib/api";
import {
  latestSupportRef,
  reportUserError,
  appendSupportRef,
  sendFeedback,
  setErrorReporter,
  supportRefFromEventId,
  tokenIdentity,
} from "@/lib/supportRef";
import { ErrorScreen } from "./components/ErrorScreen";
import { useToaster } from "./components/Toaster";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { lightTheme, darkTheme } from "./lib/theme";
import { useThemeStore, getInitialMode } from "./lib/themeStore";
import { routeTree } from "./routeTree.gen";
import { Toaster } from "./components/Toaster";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { LicenseInfo } from "@mui/x-license";
import { installChunkReload } from "./lib/chunkReload";
import * as Sentry from "@sentry/react";
import "./index.css";

// Frontend error tracking. DSN baked at build time (public by design). No
// session replay and no PII — this is a financial app; events carry only the
// user id and entity id from the session token. No-op when unset.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "unknown",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event) {
      // Every event names the support reference the user is shown (FIX-LOG 93).
      const { userId, entityId } = tokenIdentity(getToken());
      event.tags = {
        ...event.tags,
        support_ref: event.event_id ? supportRefFromEventId(event.event_id) : undefined,
        stack: window.location.hostname,
        entity_id: entityId,
        route: (event.tags?.route as string | undefined) ?? currentRouteId(),
      };
      if (userId) event.user = { id: userId };
      return event;
    },
  });
  setErrorReporter({
    capture: (error, tags) => Sentry.captureException(error, { level: "error", tags }),
    feedback: (input, tags) =>
      Sentry.captureFeedback(
        {
          message: input.message,
          url: input.url,
          associatedEventId: input.associatedEventId,
          source: "feedback_button",
        },
        { captureContext: { tags } },
      ),
  });
}
// The Feedback button (public/help-widget.js) is plain script outside React;
// it hands what the user said to Sentry through this hook.
(window as unknown as { __bynSupport?: unknown }).__bynSupport = {
  sendFeedback,
  latestSupportRef: () => latestSupportRef()?.ref ?? null,
};

// MUI X Pro license (DataGridPro etc.). Baked in at build time via the
// VITE_MUI_LICENSE_KEY Docker ARG (the key is client-side / ships in the bundle
// by design — not a secret). No-op (community watermark) when unset.
const MUI_X_KEY = import.meta.env.VITE_MUI_LICENSE_KEY;
if (MUI_X_KEY) LicenseInfo.setLicenseKey(MUI_X_KEY);

// After a redeploy, a page from the previous build reloads once when one of
// its old chunks is gone, instead of showing "Something went wrong!" (FIX-LOG 82).
installChunkReload(window, import.meta.url);

// ── Flash prevention ──────────────────────────────────────────────────────────
// Read the persisted mode synchronously BEFORE React mounts so the <html>
// element gets its `.dark` class before the first paint. Without this the
// page briefly flashes light-mode background when dark is selected.
const preloadedMode = getInitialMode();
if (preloadedMode === "dark") {
  document.documentElement.classList.add("dark");
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Global query / mutation error handlers.
 *
 * Policy: **no silent failures.** Every mutation that throws and every
 * query that errors gets a toast unless the caller explicitly opts out.
 *
 * Opt-out per call:
 *   useMutation({ ..., meta: { skipErrorToast: true } })
 *   useQuery({ ..., meta: { skipErrorToast: true } })
 *
 * Use `skipErrorToast` when the component already renders the error inline
 * (e.g. a form that shows a red Alert next to the affected field) — the
 * toast would be redundant. Otherwise leave it on. Two toasts beat zero.
 *
 * 401s are handled separately by `api.ts`'s `onResponseError` (auto-redirect
 * to /login). We skip the toast for those so the redirect lands cleanly.
 */
function isAuthError(err: unknown): boolean {
  const e = err as { status?: number; response?: { status?: number } };
  return e?.status === 401 || e?.response?.status === 401;
}

const mutationCache = new MutationCache({
  onError: (error, _vars, _ctx, mutation) => {
    if (isAuthError(error)) return; // handled by api.ts redirect
    // Reported even when the caller shows the error inline: the user saw it.
    const ref = reportUserError(error, { surface: "mutation" });
    if (mutation.meta?.skipErrorToast) return;
    const msg = appendSupportRef(errorMessage(error), ref);
    const detail = errorDetail(error);
    // Always log to console too — devtools breadcrumb for debugging.
    console.error("[mutation failed]", mutation.options.mutationKey ?? mutation.mutationId, error);
    useToaster.getState().push("error", msg, {
      action: detail?.action,
      external: detail?.external,
    });
  },
});

const queryCache = new QueryCache({
  onError: (error, query) => {
    if (isAuthError(error)) return;
    // Background refetches happen constantly — only toast (and report) the
    // FIRST failure (state.fetchFailureCount === 1) so a flaky network doesn't
    // spam the user with 50 toasts or Sentry with 50 events.
    if (query.state.fetchFailureCount > 1) return;
    const ref = reportUserError(error, { surface: "query" });
    if (query.meta?.skipErrorToast) return;
    const msg = appendSupportRef(errorMessage(error), ref);
    const detail = errorDetail(error);
    console.error("[query failed]", query.queryKey, error);
    useToaster.getState().push("error", `Couldn't load data — ${msg}`, {
      action: detail?.action,
      external: detail?.external,
    });
  },
});

const queryClient = new QueryClient({
  mutationCache,
  queryCache,
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
    // Mutations should NOT auto-retry — a failed POST that retries silently
    // can cause duplicate writes. The user sees the toast and decides.
    mutations: { retry: 0 },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Replaces the router's bare "Something went wrong!" with controlled copy,
  // a support reference and a way out (FIX-LOG 93).
  defaultErrorComponent: ({ error }) => <ErrorScreen error={error} />,
});

function currentRouteId(): string | undefined {
  try {
    const matches = router.state.matches;
    return matches[matches.length - 1]?.routeId;
  } catch {
    return undefined;
  }
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Make `meta.skipErrorToast` (and any future flags) type-safe on every
// useQuery / useMutation call.
declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { skipErrorToast?: boolean };
    mutationMeta: { skipErrorToast?: boolean };
  }
}

/**
 * Thin wrapper that syncs the Zustand theme mode to:
 *   1. The MUI ThemeProvider (so MUI components re-theme)
 *   2. document.documentElement.classList (so Tailwind dark: variants fire)
 *   3. document.documentElement.style.colorScheme (so native form controls
 *      and scrollbars respect the chosen scheme)
 */
function AppRoot() {
  const mode = useThemeStore((s) => s.mode);

  useEffect(() => {
    const html = document.documentElement;
    if (mode === "dark") {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }
    html.style.colorScheme = mode;
  }, [mode]);

  return (
    <ThemeProvider theme={mode === "dark" ? darkTheme : lightTheme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster />
        <ConfirmDialog />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={({ error, eventId }) => <ErrorScreen error={error} eventId={eventId} surface="app_error" />}>
      <AppRoot />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
